// @ts-nocheck
import * as React from 'react';
// DemoTriggerModal — captures phone, language, and personal note for demo trigger.

import { useState, useEffect } from 'react';
import Icon from '../common/Icon';
import { LANGUAGES } from '../../data/constants';

const DELIVERY_LANGUAGES = LANGUAGES.filter((language) => ['English', 'Hindi', 'Telugu'].includes(language));

export default function DemoTriggerModal({ open, parent, pillar, onClose, onRequestDemo, onCheckStatus, isWhatsAppConfirmed = false, onConfirm }) {
  const parentId = parent?.id;
  const parentPhone = parent?.phone || '';
  const parentLanguage = parent?.language || '';
  const [phone, setPhone] = useState("");
  const [lang, setLang] = useState("");
  const [note, setNote] = useState("");
  const [selectedAssetTypes, setSelectedAssetTypes] = useState([]);
  const [optInState, setOptInState] = useState('idle');
  const [sending, setSending] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [approvalQr, setApprovalQr] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const demoContent = pillar?.generatedContent || {};
  const previewTitle = demoContent.TITLE || demoContent.HEADING;
  const previewText = demoContent.MESSAGE || demoContent.ISSUE;
  const previewMeta = demoContent.CATEGORY || demoContent.MODULE;
  const availableAssetTypes = [
    previewText && 'Text',
    demoContent.AUDIO_URL && 'Audio',
    demoContent.VIDEO_URL && 'Video',
  ].filter(Boolean);
  const availableAssetKey = availableAssetTypes.join('|');

  useEffect(() => {
    if (open && parentId) {
      setPhone(parentPhone);
      setLang(DELIVERY_LANGUAGES.includes(parentLanguage) ? parentLanguage : 'English');
      setNote("");
      setSelectedAssetTypes(availableAssetKey ? availableAssetKey.split('|') : []);
      setOptInState(isWhatsAppConfirmed ? 'success' : 'idle');
      setApprovalQr(null);
      setSending(false);
      setRequestError('');
      setCheckingStatus(false);
      setStatusMessage('');
    }
  }, [open, parentId, parentPhone, parentLanguage, availableAssetKey, isWhatsAppConfirmed]);

  useEffect(() => {
    if (isWhatsAppConfirmed) setOptInState('success');
  }, [isWhatsAppConfirmed]);

  const toggleAssetType = (assetType) => {
    setSelectedAssetTypes((current) => current.includes(assetType)
      ? current.filter(item => item !== assetType)
      : [...current, assetType]);
  };

  const sendDemo = async () => {
    setSending(true);
    setRequestError('');
    try {
      const confirmed = await onCheckStatus({ phone });
      if (!confirmed) {
        const qr = await onRequestDemo({ phone, lang });
        setApprovalQr(qr);
        setOptInState('qr');
        return;
      }
      setOptInState('success');
      await onConfirm({ phone, lang, note, assetTypes: selectedAssetTypes });
    } catch (error) {
      setRequestError(error?.response?.data?.message || error?.message || 'Unable to send the demo.');
    } finally {
      setSending(false);
    }
  };

  // Staff clicks this after asking the parent to scan + send. It polls the demo status
  // endpoint (via onCheckStatus); once the parent's message has been received the opt-in
  // flips to "permission granted" and sending unlocks.
  const confirmParentSent = async () => {
    setCheckingStatus(true);
    setRequestError('');
    setStatusMessage('');
    try {
      const confirmed = await onCheckStatus({ phone });
      if (confirmed) {
        setApprovalQr(null);
        setOptInState('success');
      } else {
        setStatusMessage("We haven't received the parent's message yet. Ask them to scan the QR and tap send, then check again.");
      }
    } catch (error) {
      setRequestError(error?.response?.data?.message || error?.message || 'Unable to check approval status.');
    } finally {
      setCheckingStatus(false);
    }
  };

  if (!parent || !pillar) return (
    <div className={"scrim " + (open ? "is-open" : "")} onClick={onClose} />
  );

  return (
    <div className={"scrim " + (open ? "is-open" : "")} onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal__head">
          <div className="modal__icon" style={{ background: pillar.tint, color: pillar.color }}>
            <Icon name={pillar.icon} size={20} />
          </div>
          <div style={{ flex: 1 }}>
            <h3 className="modal__title">Trigger live demo · {pillar.name}</h3>
            <p className="modal__sub">
              {parent.name} will receive a personalised preview on WhatsApp in seconds. They can experience this exactly as their child would.
            </p>
          </div>
          <button className="icon-btn" onClick={onClose} style={{ width: 30, height: 30, borderRadius: 8 }} title="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none"><line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" /></svg>
          </button>
        </div>
        <div className="modal__body">
          <div className="field--row">
            <div className="field">
              <span className="field__label">Parent</span>
              <input className="field__input" value={parent.name} readOnly />
            </div>
            <div className="field">
              <span className="field__label">Child · Grade</span>
              <input className="field__input" value={`${parent.child} · ${parent.grade}`} readOnly />
            </div>
          </div>
          <div className="field">
            <span className="field__label">WhatsApp number</span>
            <input
              className="field__input"
              value={phone}
              onChange={(e) => { setPhone(e.target.value); setOptInState('idle'); setApprovalQr(null); setRequestError(''); setStatusMessage(''); }}
              placeholder="+91 …"
            />
          </div>
          <div className="field">
            <span className="field__label">Deliver in language</span>
            <div className="lang-row">
              {DELIVERY_LANGUAGES.map((L) => (
                <button
                  key={L}
                  className={"lang-chip " + (lang === L ? "is-on" : "")}
                  onClick={() => { setLang(L); setRequestError(''); }}
                >{L}</button>
              ))}
            </div>
          </div>
          <div className="field">
            <span className="field__label">Demo asset (preview)</span>
            <div className="demo-preview">
              {(previewMeta || previewTitle) && <div className="demo-preview__heading">
                {previewMeta && <span style={{ color: pillar.color }}>{previewMeta}</span>}
                {previewTitle && <strong>{previewTitle}</strong>}
              </div>}
              {previewText && <p>{previewText}</p>}
              {demoContent.AUDIO_URL && <audio className="demo-preview__audio" controls preload="metadata" src={demoContent.AUDIO_URL}>Audio preview is not supported by this browser.</audio>}
              {demoContent.VIDEO_URL && <video className="demo-preview__video" controls preload="metadata" src={demoContent.VIDEO_URL}>Video preview is not supported by this browser.</video>}
            </div>
          </div>
          <div className="field">
            <span className="field__label">Select assets to trigger</span>
            <div className="asset-select" role="group" aria-label="Select one or more demo assets">
              {availableAssetTypes.map((assetType) => {
                const selected = selectedAssetTypes.includes(assetType);
                return (
                  <button
                    key={assetType}
                    type="button"
                    className={"asset-select__chip " + (selected ? "is-on" : "")}
                    aria-pressed={selected}
                    onClick={() => toggleAssetType(assetType)}
                  >
                    {selected && <Icon name="check" size={13} />}
                    {assetType}
                  </button>
                );
              })}
            </div>
            {selectedAssetTypes.length === 0 && <span className="asset-select__error" role="alert">Select at least one asset.</span>}
          </div>
          <div className="field">
            <span className="field__label">Personal note (optional)</span>
            <input
              className="field__input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={`Add a personal note for ${parent.name}…`}
            />
          </div>
        </div>
        {optInState === 'success' ? (
          <div className="demo-permission-granted" role="status" style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '0 24px 4px', padding: '8px 12px', borderRadius: 8, background: '#e7f7ec', color: '#137a3f', fontSize: 13, fontWeight: 600 }}>
            <Icon name="check" size={14} /> Permission granted — the parent has opted in. You can send the demo now.
          </div>
        ) : null}
        <div className="modal__foot">
          <button className="cta-pill cta-pill--ghost" onClick={onClose}>Cancel</button>
          <button
            className="cta-pill cta-pill--wa"
            onClick={sendDemo}
            disabled={sending}
          >
            {sending ? <span className="demo-btn-spinner" aria-hidden="true" /> : <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20.5 3.5A11 11 0 0 0 3.6 17.6L2 22l4.5-1.5A11 11 0 1 0 20.5 3.5z" /></svg>}
            {sending ? 'Sending…' : 'Send on WhatsApp'}
          </button>
        </div>
        {requestError ? <div className="demo-request-error" role="alert">{requestError}</div> : null}
      </div>
      {approvalQr && optInState !== 'success' && <div className="demo-qr-layer" onClick={(event) => { event.stopPropagation(); setApprovalQr(null); }}>
        <div className="demo-qr-dialog" role="dialog" aria-modal="true" aria-labelledby="demo-qr-title" onClick={(event) => event.stopPropagation()}>
          <button className="demo-qr-dialog__close" type="button" onClick={() => setApprovalQr(null)} aria-label="Close QR code">×</button>
          <div className="demo-qr-dialog__icon"><Icon name="comms" size={20} /></div>
          <div className="demo-approval-qr__copy"><strong id="demo-qr-title">Parent approval required</strong><span>Ask the parent to scan this QR code or open the WhatsApp link. Sending will unlock after confirmation.</span></div>
          {approvalQr.qrImage ? <img className="demo-approval-qr__image" src={approvalQr.qrImage} alt="WhatsApp parent approval QR code" /> : null}
          {approvalQr.token ? <span className="demo-qr-dialog__reference">Reference: {approvalQr.token}</span> : null}
          {approvalQr.expiresInSeconds ? <span className="demo-qr-dialog__expiry">Valid for {Math.round(approvalQr.expiresInSeconds / 3600)} hours</span> : null}
          {approvalQr.waLink ? <a className="demo-approval-qr__link" href={approvalQr.waLink} target="_blank" rel="noreferrer">Open approval in WhatsApp</a> : null}
          <button
            className="cta-pill cta-pill--wa"
            type="button"
            onClick={confirmParentSent}
            disabled={checkingStatus}
            style={{ marginTop: 12, width: '100%', justifyContent: 'center' }}
          >
            {checkingStatus ? <span className="demo-btn-spinner" aria-hidden="true" /> : <Icon name="check" size={14} />}
            {checkingStatus ? 'Checking…' : 'Parent has sent the message'}
          </button>
          {statusMessage ? <span className="demo-qr-dialog__status" role="status" style={{ display: 'block', marginTop: 8, fontSize: 12, color: '#b45309' }}>{statusMessage}</span> : null}
        </div>
      </div>}
    </div>
  );
}
